import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";
import Navbar from "../components/Navbar";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    avatar: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [favorites, setFavorites] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoadingFavs(true);
      const res = await api.get("/favorites");
      setFavorites(res.data.products || []);

      // Update local storage cache for other components
      const favIds = (res.data.products || []).map((p) => p._id);
      localStorage.setItem("favorites", JSON.stringify(favIds));
    } catch (error) {
      console.error("Lỗi khi tải sản phẩm yêu thích:", error);
    } finally {
      setLoadingFavs(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get("/profile");
      const user = res.data;
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        avatar: user.avatar || "",
      }));
      // Update local storage just in case
      localStorage.setItem("user", JSON.stringify(user));
    } catch (error) {
      console.error("Lỗi khi tải thông tin:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size limit (e.g., 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMsg({ type: "error", text: "Kích thước ảnh không được vượt quá 2MB" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, avatar: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setFormData({ ...formData, avatar: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (
      formData.newPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      setMsg({ type: "error", text: "Mật khẩu xác nhận không khớp!" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        avatar: formData.avatar,
      };

      if (formData.oldPassword && formData.newPassword) {
        payload.oldPassword = formData.oldPassword;
        payload.newPassword = formData.newPassword;
      }

      const res = await api.put("/profile", payload);
      setMsg({
        type: "success",
        text: res.data.message || "Cập nhật thành công!",
      });

      // Clear password fields on success
      setFormData((prev) => ({
        ...prev,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      // Update local storage user object so Navbar updates
      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
        // Force an event to trigger other components if needed, or window.location.reload()
      }
    } catch (error) {
      console.error(error);
      const text =
        error.response?.data?.error || "Cập nhật thất bại. Vui lòng thử lại.";
      setMsg({ type: "error", text });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center transition-colors duration-300">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>

      <div 
        className="min-h-screen bg-gray-50 dark:bg-slate-950 pt-16 md:pt-40 pb-12 px-2 sm:px-6 lg:px-8 transition-colors duration-300"
        style={{ 
          zoom: "0.8",
          MozTransform: "scale(0.8)",
          MozTransformOrigin: "top center"
        }}
      >
        <Navbar />
        <div className="max-w-3xl mx-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
            <div className="px-4 py-5 sm:px-10 sm:py-8 border-b border-gray-100 dark:border-slate-800">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Hồ sơ cá nhân
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Quản lý thông tin hồ sơ và bảo mật tài khoản của bạn.
              </p>
            </div>

            <div className="px-4 py-5 sm:px-10 sm:py-8">
              {msg.text && (
                <div
                  className={`mb-5 p-3 sm:p-4 rounded-xl flex items-center gap-2 sm:gap-3 ${msg.type === "error" ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400" : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"}`}
                >
                  <span className="material-symbols-outlined text-[20px] sm:text-[24px]">
                    {msg.type === "error" ? "error" : "check_circle"}
                  </span>
                  <p className="text-xs sm:text-sm font-medium">{msg.text}</p>
                </div>
              )}

              {/* Voucher Link */}
              <div
                onClick={() => navigate("/vouchers")}
                className="mb-6 p-4 sm:p-6 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-2xl text-white flex items-center justify-between cursor-pointer hover:shadow-lg transition-all active:scale-[0.98] shadow-blue-200/50 dark:shadow-none"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2.5 bg-white/20 rounded-xl">
                    <span className="material-symbols-outlined text-[24px] sm:text-[32px]">
                      confirmation_number
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold">
                      Kho Voucher của bạn
                    </h3>
                    <p className="text-[11px] sm:text-sm text-blue-100 italic">
                      Kiểm tra ngay các mã giảm giá bạn đang có!
                    </p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-[20px] sm:text-[24px]">
                  chevron_right
                </span>
              </div>

              {/* Favorites Section */}
              <div 
                onClick={() => navigate("/wishlist")}
                className="mb-6 p-4 sm:p-6 bg-gradient-to-r from-red-500 to-pink-500 dark:from-red-600 dark:to-pink-600 rounded-2xl text-white shadow-md shadow-red-100 dark:shadow-none cursor-pointer hover:shadow-lg transition-all active:scale-[0.98]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2.5 bg-white/20 rounded-xl">
                      <span className="material-symbols-outlined text-[24px] sm:text-[32px]">
                        favorite
                      </span>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold">
                        Sản phẩm yêu thích
                      </h3>
                      <p className="text-[11px] sm:text-sm text-red-100">
                        {favorites.length} sản phẩm bạn đã thả tim
                      </p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-[20px] sm:text-[24px]">
                    chevron_right
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                {/* Avatar Section */}
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 pb-6 sm:pb-8 border-b border-gray-100 dark:border-slate-800">
                  <div className="relative group/avatar cursor-pointer">
                    <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden border-4 border-white dark:border-slate-800 shadow-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center transition-colors">
                      {formData.avatar ? (
                        <img
                          src={formData.avatar}
                          alt="Avatar"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl sm:text-3xl font-bold text-gray-400 dark:text-slate-500">
                          {formData.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <label className="absolute inset-0 flex items-center justify-center bg-black/50 text-white rounded-full opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer">
                      <span className="material-symbols-outlined text-[20px] sm:text-[24px]">
                        photo_camera
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleAvatarChange}
                      />
                    </label>
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                      Ảnh đại diện
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2 sm:mb-3">
                      JPEG, PNG. Tối đa 2MB.
                    </p>
                    <div className="flex gap-2 justify-center sm:justify-start">
                      <label className="px-3 py-1.5 text-[10px] sm:text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 cursor-pointer transition-colors">
                        Đổi ảnh
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleAvatarChange}
                        />
                      </label>
                      {formData.avatar && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="px-3 py-1.5 text-[10px] sm:text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                        >
                          Xóa ảnh
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                      Họ và tên
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                      Email
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        disabled
                        value={formData.email}
                        className="w-full px-4 py-2 sm:py-2.5 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-gray-400 rounded-lg outline-none cursor-not-allowed text-sm sm:text-base"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-[18px]">
                        lock
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] sm:text-xs text-gray-400 dark:text-slate-500 italic ml-1 transition-colors">
                      Email không thể thay đổi
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                      Số điện thoại
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="VD: 0912345678"
                      className="w-full px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 pt-6 sm:pt-8 mt-6 sm:mt-8">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Đổi mật khẩu (không bắt buộc)
                  </h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                        Mật khẩu hiện tại
                      </label>
                      <input
                        type="password"
                        name="oldPassword"
                        value={formData.oldPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                        Mật khẩu mới
                      </label>
                      <input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 ml-1">
                        Xác nhận mật khẩu mới
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm sm:text-base"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-2 sm:pt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-wider text-xs sm:text-sm shadow-lg shadow-blue-200 dark:shadow-none"
                  >
                    {saving ? (
                      <span className="material-symbols-outlined animate-spin text-[20px]">
                        refresh
                      </span>
                    ) : (
                      <span className="material-symbols-outlined text-[20px]">
                        save
                      </span>
                    )}
                    Lưu thay đổi
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
